import React, { useMemo, useState } from "react";
import { ArrowRight, Plus } from "lucide-react";
import { useT, Namespace } from "@racoon/i18n";
import {
  Button,
  FieldRow,
  Input,
  SearchablePopover,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { matchesAnySearch } from "@racoon/lib";
import { useCreateTeamMutation, useUpdateTeamMutation } from "~/shared/api/team/team.grpc";
import {
  useListUsersQuery,
  roleEnumToKey,
  roleKeyToEnum,
  type RoleKey,
} from "~/shared/api/user/user.grpc";
import { getStaffRoleOptions } from "~/shared/lib/role-options";
import { IcCancel } from "@racoon/icons";

type Team = ConnectProto.Team;
type User = ConnectProto.User;

const userName = (u: User) => [u.firstName, u.lastName].filter(Boolean).join(" ") || `#${u.id}`;

interface TeamFormProps {
  team: Team | null;
  ownerOrganizationId: number;
  onClose: () => void;
  onDelete: (team: Team) => void;
}

/** Body of the create / edit team modal, opened through ModalProvider. */
export const TeamForm: React.FC<TeamFormProps> = ({
  team,
  ownerOrganizationId,
  onClose,
  onDelete,
}) => {
  const t = useT();
  const { toast } = useToast();
  const [name, setName] = useState(team?.name ?? "");
  const [role, setRole] = useState<RoleKey | "">(team ? roleEnumToKey(team.userRole) : "");
  const [members, setMembers] = useState<User[]>(team?.users ?? []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [createTeam, { isLoading: isCreating }] = useCreateTeamMutation();
  const [updateTeam, { isLoading: isUpdating }] = useUpdateTeamMutation();
  const isSaving = isCreating || isUpdating;

  const roleEnum = roleKeyToEnum(role);
  // `currentData`: never offer users of a previous role while the new one loads or fails.
  const { currentData: usersResponse, isFetching: usersLoading } = useListUsersQuery(
    {
      ownerOrganizationIds: [BigInt(ownerOrganizationId)],
      roles: roleEnum !== undefined ? [roleEnum] : [],
    },
    { skip: roleEnum === undefined },
  );

  const candidates = useMemo(() => {
    const taken = new Set(members.map((m) => m.id));
    return (usersResponse?.users ?? []).filter(
      (u) => !taken.has(u.id) && matchesAnySearch([userName(u), u.phoneNumber], search),
    );
  }, [usersResponse, members, search]);

  const handleRoleChange = (value: string) => {
    setRole(value as RoleKey);
    setMembers([]);
  };

  const handleSubmit = async () => {
    if (roleEnum === undefined) return;
    const userIds = members.map((m) => m.id);
    try {
      if (team) {
        await updateTeam({ id: team.id, name: name.trim(), userIds }).unwrap();
        toast({ title: t(Namespace.Settings, "teams.dialog.updateSuccess") });
      } else {
        await createTeam({
          name: name.trim(),
          userRole: roleEnum,
          ownerOrganizationId: BigInt(ownerOrganizationId),
          userIds,
        }).unwrap();
        toast({ title: t(Namespace.Settings, "teams.dialog.createSuccess") });
      }
      onClose();
    } catch {
      // GlobalErrorToaster shows the API error; keep the modal open for a retry.
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4 p-4">
        <FieldRow label={t(Namespace.Settings, "teams.dialog.name")} htmlFor="team-name">
          <Input
            id="team-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t(Namespace.Settings, "teams.dialog.namePlaceholder")}
            className="h-8"
          />
        </FieldRow>

        <FieldRow label={t(Namespace.Settings, "teams.dialog.role")} htmlFor="team-role">
          <Select value={role} onValueChange={handleRoleChange} disabled={team !== null}>
            <SelectTrigger id="team-role" className="h-8">
              <SelectValue placeholder={t(Namespace.Settings, "teams.dialog.rolePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {getStaffRoleOptions(t).map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldRow>

        <div className="flex max-h-72 min-h-40 flex-wrap content-start gap-2 overflow-y-auto rounded-lg border border-gray-200 p-2">
          {members.length === 0 && (
            <p className="self-center px-1 text-sm text-gray-500">
              {t(Namespace.Settings, "teams.dialog.noMembers")}
            </p>
          )}
          {members.map((m) => (
            <span
              key={m.id.toString()}
              className="inline-flex h-6 items-center gap-1 rounded-2xl bg-brand-blue py-1 pl-3 pr-1 text-xs font-medium text-white"
            >
              {userName(m)}
              <button
                type="button"
                aria-label={t(Namespace.Settings, "teams.dialog.removeMember", {
                  name: userName(m),
                })}
                onClick={() => setMembers((prev) => prev.filter((x) => x.id !== m.id))}
                className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <IcCancel className="size-4" />
              </button>
            </span>
          ))}
        </div>

        <SearchablePopover
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          searchValue={search}
          onSearchChange={setSearch}
          inputId="team-member-search"
          placeholder={t(Namespace.Settings, "teams.dialog.searchMember")}
          trigger={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={roleEnum === undefined}
              className="gap-1 self-start text-sm text-brand-blue"
            >
              <Plus className="size-4" />
              {t(Namespace.Settings, "teams.dialog.addMember")}
              <ArrowRight className="size-4" />
            </Button>
          }
        >
          {usersLoading ? (
            <p className="p-4 text-center text-sm text-gray-500">
              {t(Namespace.Settings, "messages.loading")}
            </p>
          ) : candidates.length === 0 ? (
            <p className="p-4 text-center text-sm text-gray-500">
              {t(Namespace.Settings, "teams.dialog.noMembersToAdd")}
            </p>
          ) : (
            candidates.map((u) => (
              <button
                key={u.id.toString()}
                type="button"
                onClick={() => setMembers((prev) => [...prev, u])}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              >
                {userName(u)}
              </button>
            ))
          )}
        </SearchablePopover>
      </div>

      <div
        className={`flex border-t border-gray-200 p-4 ${team ? "justify-between" : "justify-end"}`}
      >
        {team && (
          <Button variant="solidRed" onClick={() => onDelete(team)} disabled={isSaving}>
            {t(Namespace.Buttons, "delete")}
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          disabled={isSaving || roleEnum === undefined || !name.trim()}
        >
          {team ? t(Namespace.Buttons, "save") : t(Namespace.Settings, "teams.dialog.createButton")}
        </Button>
      </div>
    </div>
  );
};
